import { MutableRefObject, useCallback, useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { WebSocketContext } from '../../websocket-context/websocket-provider';
import { RangeEnum } from '../../../model/range.enum';
import Utils from '../../../utils/immersive_utils';
import {
  CMVOL_SERIES_HISTORICAL_ID,
  CMVOL_SERIES_ID,
  PERPETUAL_ID,
} from '../market-data.constants';
import { VolumeMultipleRequest } from '../../model/volumeData';
import { QueryData } from '../../model/interfaces/volume_data';
import { SourceTypeEnum } from '../../../model/source_type.enum';
import { SeriesState } from '../series-reducer';
import { IChartApi } from 'lightweight-charts';

export type IFetchVolsHistorical = {
  endDate: Date;
  queries: QueryData[];
  newRange?: RangeEnum;
  options?: {
    isBackward?: boolean;
    isRefresh?: boolean;
    isNewVol?: boolean;
    isClosestForwardData?: boolean;
    calendarStart?: Date;
  };
};

export function useVolData(
  currency: string,
  providerKey: string | undefined,
  range: RangeEnum,
  series: MutableRefObject<SeriesState>,
  updateSeries: (
    id: string,
    data: { time: number; value: number }[],
    opts?: { isBackward?: boolean; isRefresh?: boolean; isClosestForwardData?: boolean },
  ) => void,
  appendSeriesTick: (id: string, tick: any) => void,
  chart?: MutableRefObject<IChartApi | undefined>,
  chartExtra?: MutableRefObject<IChartApi | undefined>,
) {
  const { sendMessage, subscribeToChannel, isConnected } = useContext(WebSocketContext);
  const [loading, setLoading] = useState(false);
  const [isFetchingCmvolSeries, setIsFetchingCmvolSeries] = useState(false);
  const isToStopGraphsSyncRef = useRef<boolean>(false);
  const isVolsFetchBlockedRef = useRef<boolean>(false);

  const makeId = (id: string) => `${id}_${currency}${providerKey ? `-${providerKey}` : ''}`;

  const updateChartInteractivity = ({ isBlocked }: { isBlocked: boolean }) => {
    if (!chart?.current || !chartExtra?.current) return;

    chart.current?.applyOptions({ handleScroll: !isBlocked, handleScale: !isBlocked });
    chartExtra.current?.applyOptions({ handleScroll: !isBlocked, handleScale: !isBlocked });
  };

  const fetchVolsHistorical = useCallback(
    ({ endDate, queries, newRange, options }: IFetchVolsHistorical) => {
      if (!isConnected) return;

      if (!queries || queries.length === 0) {
        setLoading(false);
        setIsFetchingCmvolSeries(false);
        isToStopGraphsSyncRef.current = false;
        isVolsFetchBlockedRef.current = false;
        updateChartInteractivity({ isBlocked: false });
        return;
      }

      const { isBackward, isClosestForwardData, isRefresh, calendarStart, isNewVol } =
        options || {};

      const isResetCall = Boolean(newRange) || Boolean(isRefresh);
      if (isVolsFetchBlockedRef.current && !isResetCall) {
        setLoading(false);
        setIsFetchingCmvolSeries(false);
        return;
      } else if (isResetCall) {
        isVolsFetchBlockedRef.current = false;
        isToStopGraphsSyncRef.current = false;
      }

      if (isFetchingCmvolSeries) return;

      const rangeToUse = newRange || range;
      const interval = Utils.getIntervalByRange(rangeToUse);

      let init: Date;
      let to: Date;

      if (calendarStart) {
        init = calendarStart;
        to = endDate;
      } else if (isBackward) {
        init = Utils.getInitDateByRange(endDate, rangeToUse);
        to = endDate;
      } else if (isClosestForwardData) {
        const forward = Utils.getEndDateDateByRange(endDate, rangeToUse);
        init = endDate;
        to = Utils.updateDateToInterval(forward, interval);
      } else {
        const perp = series.current[PERPETUAL_ID];
        if (perp && perp.data.length > 0 && !isRefresh) {
          const earliestSec = Math.min(...perp.data.map((pt) => pt.time));
          init = new Date(earliestSec * 1000);
        } else {
          init = Utils.getInitDateByRange(endDate, rangeToUse);
        }
        to = Utils.updateDateToInterval(endDate, interval);
      }

      const id = isBackward ? makeId(CMVOL_SERIES_HISTORICAL_ID) : makeId(CMVOL_SERIES_ID);

      const req = new VolumeMultipleRequest(
        id,
        init.toISOString(),
        to.toISOString(),
        interval,
        SourceTypeEnum.DERIBIT,
        currency!,
        queries,
        'SVI',
      );

      if (isBackward) isToStopGraphsSyncRef.current = true;

      setIsFetchingCmvolSeries(true);
      updateChartInteractivity({ isBlocked: true });

      try {
        sendMessage(JSON.stringify(req));
      } catch {
        setLoading(false);
        setIsFetchingCmvolSeries(false);
        isToStopGraphsSyncRef.current = false;
        updateChartInteractivity({ isBlocked: false });
        return;
      }

      subscribeToChannel(id, async (resp: any) => {
        setLoading(false);
        if (!resp.result && resp.error) {
          if (!isNewVol) {
            isVolsFetchBlockedRef.current = true;
            isToStopGraphsSyncRef.current = true;
          }
          updateChartInteractivity({ isBlocked: false });
          setIsFetchingCmvolSeries(false);
          return;
        }

        if (!resp.result) {
          isToStopGraphsSyncRef.current = false;
          updateChartInteractivity({ isBlocked: false });
          setIsFetchingCmvolSeries(false);
          return;
        }

        const rows = resp.result as any[][];

        for (let idx = 0; idx < queries.length; idx++) {
          const q = queries[idx];
          const sid = `CMVOL_${q.label}_${q.tenor}_${currency}`;
          const points = rows.map((r) => ({
            time: moment.utc(r[0] as string, 'YYYY-MM-DD HH:mm:ss').unix(),
            value: sid.includes('_FUTURE') ? r[idx + 1] : r[idx + 1] * 100,
          }));

          await new Promise<void>((resolve) => {
            updateSeries(sid, points, { isBackward, isRefresh });
            resolve();
          });
        }

        if (chart?.current && chartExtra?.current && !isBackward && !isNewVol) {
          chart.current.timeScale().fitContent();
          const logical = chart.current.timeScale().getVisibleLogicalRange();
          if (logical) {
            chartExtra.current.timeScale().setVisibleLogicalRange(logical);
          }
        }

        isToStopGraphsSyncRef.current = false;
        updateChartInteractivity({ isBlocked: false });
        setIsFetchingCmvolSeries(false);
      });
    },
    [isConnected, series, currency, sendMessage, range, isFetchingCmvolSeries],
  );

  const subscribeLive = useCallback(() => {
    if (!isConnected) return;

    const req = {
      id: makeId('CMVOL_SUBSCRIPTION'),
      method: 'subscribe.latest_vol_surface',
      jsonrpc: '2.0',
      params: {
        type: 'CMVOLSURFACE',
        token: currency,
        timestamp: new Date().toISOString(),
        model: 'SVI',
      },
    };
    sendMessage(JSON.stringify(req));
    subscribeToChannel(makeId('CMVOL_SUBSCRIPTION'), (resp: any) => {
      const data = resp.params?.data;
      if (!data) return;

      const { timestamp, tenors } = data as {
        timestamp: string;
        tenors: Record<string, Record<string, number>>;
      };

      if (!timestamp || !tenors || typeof tenors !== 'object') return;

      const ts = moment.utc(timestamp, 'YYYY-MM-DD HH:mm:ss').unix();

      Object.entries(tenors ?? {}).forEach(([tenorKey, volMap]) => {
        if (!volMap || typeof volMap !== 'object') return;

        Object.entries(volMap).forEach(([lbl, rawVal]) => {
          if (rawVal == null) return;

          const sid = `CMVOL_${lbl}_${tenorKey}_${currency}`;
          const serie = series.current[sid];
          if (!serie) return;

          const value = sid.includes('_FUTURE') ? rawVal : rawVal * 100;

          const tick = { time: ts, value };
          appendSeriesTick(sid, tick);
        });
      });
    });
  }, [isConnected, currency, providerKey, sendMessage, subscribeToChannel, appendSeriesTick]);

  useEffect(() => {
    return () => {
      setLoading(false);
    };
  }, []);

  useEffect(() => {
    isVolsFetchBlockedRef.current = false;
    isToStopGraphsSyncRef.current = false;
  }, [currency, providerKey]);

  return {
    loading,
    setLoading,
    isFetchingCmvolSeries,
    fetchVolsHistorical,
    subscribeLive,
    isToStopGraphsSyncRef,
  };
}
